import React, { useCallback, useEffect, useMemo } from "react";
import { BlockOption, BlockSubTypes, BlockTypes, getBlockOption } from "../../helpers/blocks";
import { Skeleton, Switch, TextField } from "@mui/material";
import { SurveyLayoutField } from "../Fields/SurveyLayoutField";
import { SurveyPreviewField } from "../Fields/SurveyPreviewField";
import { SurveyOptionsField } from "../Fields/SurveyOptionsField";
import { BlockToEdit, MainSurveyStore } from "../../hooks/useMainSurvey";
import { useMainSurveyData } from "../../hooks/useMainSurveyData";
import { mainSurveyClient } from "../../client/mainSurveyClient";
import { Surveys } from "modules/Surveys/surveys";
import { SurveyRangeField } from "../Fields/SurveyRangeField";
import { BlockFieldTypes, BlockValues, RangeField, SurveyLayouts, blockToBlockValues } from "../../helpers/blockForm";
import { Conditions } from "modules/Conditions/components/Conditions";
import { SurveyBlocks } from "../../surveyBlocks";
import { Wysiwyg } from "shared/components/Inputs/Wysiwyg";
import { FormInput } from "shared/components/Inputs/FormInput";
import { RelatedSupportingInfos } from "modules/SupportingInfo/components/BlockFormSupportingInfo/RelatedSupportingInfos";
import { unset } from "lodash";
import { QuestionLayoutField } from "../Fields/QuestionLayoutField";
import { TagsField } from "../Fields/TagsField";
import { replaceComma } from "shared/helpers/string";
import { ManageTableButton } from "shared/components/Form/ManageTableButton";
import { useBlockFormValues } from "../../hooks/useBlockFormValues";
import { isMainSurvey, isSurvey as isSurveyType } from "../../helpers/blockTree";
import { BlockFormHeader } from "./BlockFormHeader";
import {
  AxisOptions,
  createFormattingRule,
  deleteFormattingInstructions,
  FormattingRule,
  FormattingRuleType,
  getFormattingRule,
} from "@coolset/cs-ui";
import { ExportInstructionsSection } from "./ExportInstructions";
import { ExpressionInputV2 } from "modules/Expressions/components/ExpressionInputV2";

export const BlockForm = (props: BlockFormProps) => {
  const {
    mainSurveyStore: {
      mainSurvey,
      blockToEdit: mainBlockToEdit,
      resetBlockToEdit,
      resetSubBlockToEdit,
      setMainSurveyTree,
      setIsSaving,
      updateMainSurveyName,
      updateBlock,
      setIsTableEditOpen,
      subBlockToEdit,
      forceSave,
      setForceSave,
      setTableBlocks,
      tableBlocks,
    },
    isSubBlock,
    handleAfterDeleteBlock,
    setIsAwaitingBlockUpdate,
  } = props;

  const blockToEdit = useMemo<BlockToEdit | null>(
    () => (isSubBlock ? subBlockToEdit : mainBlockToEdit),
    [isSubBlock, mainBlockToEdit, subBlockToEdit]
  );
  const { surveyBlock, isLoading } = useMainSurveyData(blockToEdit?.parentSurveyId || "", blockToEdit?.id);

  const isMain = useMemo<boolean>(() => !!surveyBlock && isMainSurvey(surveyBlock), [surveyBlock]);
  const isSurvey = useMemo<boolean>(() => !!surveyBlock && isSurveyType(surveyBlock), [surveyBlock]);

  const { blockValues, isSaving, setBlockValues, updateBlockApi, setLastBlockValues, error } = useBlockFormValues(
    blockToEdit?.id ?? mainSurvey?.id,
    blockToEdit?.parentSurveyId,
    isMain
  );

  const blockOption = useMemo<BlockOption>(
    () => getBlockOption(blockToEdit?.type || BlockTypes.MainSurvey),
    [blockToEdit]
  );

  const handleDeleteBlock = useCallback(() => {
    if (!blockToEdit?.id) return;

    setIsAwaitingBlockUpdate?.(true);
    try {
      mainSurveyClient.deleteSurveyBlock(blockToEdit.parentSurveyId, blockToEdit.id);

      if (isSubBlock) {
        const newTableBlocks = [...(tableBlocks || [])].filter((block) => block.id !== blockToEdit.id);
        setTableBlocks(newTableBlocks);
        resetSubBlockToEdit(newTableBlocks);
        handleAfterDeleteBlock?.(String(blockToEdit.id));
      } else {
        setMainSurveyTree((prev) => {
          unset(prev, blockToEdit.blockPath!);
          return { ...prev };
        });
        resetBlockToEdit();
      }
    } catch (error) {
      console.error(error);
      setIsAwaitingBlockUpdate?.(false);
    }
  }, [
    blockToEdit,
    isSubBlock,
    tableBlocks,
    setTableBlocks,
    resetSubBlockToEdit,
    setMainSurveyTree,
    resetBlockToEdit,
    handleAfterDeleteBlock,
    setIsAwaitingBlockUpdate,
  ]);

  const handleFieldChange = useCallback(
    (fieldKey: string, value?: string | string[] | boolean | RangeField | FormattingRule[]) => {
      setBlockValues((prevBlockValues) => ({ ...prevBlockValues, [fieldKey]: value }));
    },
    [setBlockValues]
  );

  const handleSurveyLayoutChange = useCallback(
    (value: string) => {
      const axisRule = getFormattingRule(blockValues.formattingInstructions, {
        ruleType: FormattingRuleType.Axis,
        axis: AxisOptions.Y,
      });

      handleFieldChange(BlockFieldTypes.Layout, value);

      // TODO - check if we need to delete the axis rule
      if ([SurveyLayouts.Bar, SurveyLayouts.Pie].includes(value as SurveyLayouts)) {
        if (axisRule) {
          const [newRules] = deleteFormattingInstructions(blockValues.formattingInstructions, axisRule.ruleId);
          handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
        }
      } else if ([SurveyLayouts.Scatter, SurveyLayouts.Line].includes(value as SurveyLayouts)) {
        if (!axisRule) {
          const [newRules] = createFormattingRule(blockValues.formattingInstructions, {
            ruleType: FormattingRuleType.Axis,
            position: "B",
            axis: AxisOptions.Y,
          });
          handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
        }
      }
    },
    [blockValues, handleFieldChange]
  );

  const handleExpressionChange = useCallback(
    (value: { expression?: string; fields: string[] }) => {
      handleFieldChange(BlockFieldTypes.Fields, value.fields || []);
      handleFieldChange(BlockFieldTypes.Expression, value.expression);
    },
    [handleFieldChange]
  );

  const fieldsWithTitle = useMemo(
    () => [{ key: BlockFieldTypes.Title, title: "Title" }, ...blockOption.fields].filter((field) => !field.isAdvanced),
    [blockOption.fields]
  );

  const fieldId = useMemo<string | null | undefined>(() => {
    if (!surveyBlock) {
      return;
    }

    return isMain ? null : (surveyBlock as SurveyBlocks.Block).contentObject.fieldId;
  }, [isMain, surveyBlock]);

  const fieldElements = useMemo(
    () =>
      fieldsWithTitle.map((field, index) => {
        let fieldInput;
        switch (field.key) {
          case BlockFieldTypes.Title:
          case BlockFieldTypes.Subtitle:
          case BlockFieldTypes.Tooltip:
            fieldInput = (
              <TextField
                value={blockValues[field.key] || ""}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                inputProps={{ maxLength: SurveyBlocks.BlockContentCharLimit[field.key] }}
              />
            );
            break;
          case BlockFieldTypes.IsRequired:
          case BlockFieldTypes.AllowDecimal:
            fieldInput = (
              <Switch
                className="module-field-switch"
                checked={!!blockValues[field.key]}
                onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              />
            );
            break;
          case BlockFieldTypes.Layout:
            fieldInput = (
              <SurveyLayoutField
                onChange={(value) => handleSurveyLayoutChange(value)}
                selectedValue={blockValues[field.key]}
                blockType={blockToEdit?.type}
              />
            );
            break;
          case BlockFieldTypes.QuestionLayout:
            fieldInput = (
              <QuestionLayoutField
                onChange={(value) => handleFieldChange(field.key, value)}
                selectedValue={blockValues[field.key]}
              />
            );
            break;
          case BlockFieldTypes.Preview:
            fieldInput = (
              <SurveyPreviewField
                onChange={(value) => handleFieldChange(field.key, value)}
                selectedValue={blockValues[field.key]}
              />
            );
            break;
          case BlockFieldTypes.Options:
            fieldInput = (
              <SurveyOptionsField
                options={blockValues[field.key] || []}
                onChange={(value: string[]) => handleFieldChange(field.key, value)}
                onBeforeChange={(value) => replaceComma(value)}
              />
            );
            break;
          case BlockFieldTypes.Legend:
            fieldInput = (
              <SurveyOptionsField
                options={blockValues[field.key] || []}
                onChange={(value: string[]) => handleFieldChange(field.key, value)}
              />
            );
            break;
          case BlockFieldTypes.Range:
            fieldInput = (
              <SurveyRangeField
                value={blockValues[field.key]}
                onChange={(value) => handleFieldChange(field.key, value)}
              />
            );
            break;
          case BlockFieldTypes.Content:
            fieldInput = (
              <Wysiwyg savedData={blockValues[field.key]} onChange={(value) => handleFieldChange(field.key, value)} />
            );
            break;
          case BlockFieldTypes.Tags:
            fieldInput = <TagsField surveyId={mainSurvey!.id} value={mainSurvey?.tags ?? []} />;
            break;
          case BlockFieldTypes.Expression:
            fieldInput = (
              <ExpressionInputV2
                key={blockValues.fieldId}
                onChange={handleExpressionChange}
                value={{ expression: blockValues.expression, fields: blockValues.fields }}
                error={error?.expression?.expression?.[0] ?? error?.fields?.fields?.[0]}
              />
            );
            break;

          default:
            break;
        }

        return (
          <FormInput
            key={`${field.key}_${index}`}
            subtitle={field.subtitle}
            title={field.title}
            isOptional={field.isOptional}
            fieldInput={fieldInput}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blockValues, fieldsWithTitle, handleFieldChange, mainSurvey, error]
  );

  useEffect(() => {
    if (!surveyBlock) {
      return;
    }

    const values = {} as BlockValues;
    if (isMain) {
      values.title = (surveyBlock as Surveys.Survey).title;
      values.displayType = (surveyBlock as Surveys.Survey).displayType;
      values.isExportable = (surveyBlock as Surveys.Survey).isExportable;
      values.exportInstructions = (surveyBlock as Surveys.Survey).exportInstructions;
    } else {
      Object.assign(values, blockToBlockValues(surveyBlock));
    }

    setLastBlockValues(values);
    setBlockValues(values);
  }, [fieldsWithTitle, isMain, setBlockValues, setLastBlockValues, surveyBlock]);

  useEffect(() => {
    updateBlockApi(false, isMain ? updateMainSurveyName : updateBlock);
  }, [blockValues, isMain, updateBlock, updateBlockApi, updateMainSurveyName]);

  useEffect(() => {
    if (!forceSave) return;

    updateBlockApi(true, isMain ? updateMainSurveyName : updateBlock);
    setForceSave(false);
  }, [forceSave, isMain, setForceSave, updateBlock, updateBlockApi, updateMainSurveyName]);

  useEffect(() => setIsSaving(isSaving), [isSaving, setIsSaving]);

  const isChart =
    !isMain && !!surveyBlock && (surveyBlock as SurveyBlocks.Block).contentObject.type === BlockSubTypes.Chart;
  const isTable =
    !isMain && !!surveyBlock && (surveyBlock as SurveyBlocks.Block).contentObject.type === BlockSubTypes.Table;
  const isTableCellSubBlock = isSubBlock && (surveyBlock as SurveyBlocks.Block)?.survey?.type === BlockSubTypes.Table;

  const allowTableEdit = isChart || isTable;

  return (
    <div className="layout-block-form">
      {!isLoading && !isTableCellSubBlock && (
        <BlockFormHeader
          blockOption={blockOption}
          fieldId={fieldId}
          allowDelete={!!blockToEdit?.id}
          onDeleteBlock={handleDeleteBlock}
        />
      )}
      {isLoading ? (
        <div className="_d-grid-md-gap">
          <Skeleton />
          <Skeleton width="80%" />
          <Skeleton width="90%" />
        </div>
      ) : (
        <>
          {fieldElements}
          {allowTableEdit && <ManageTableButton onClick={() => setIsTableEditOpen(true)} text="Manage dataset" />}

          {!isMain && <Conditions isSubBlock={!!isSubBlock} mainSurveyStore={props.mainSurveyStore} />}
          {!isMain && <RelatedSupportingInfos isSubBlock={!!isSubBlock} mainSurveyStore={props.mainSurveyStore} />}
          {(isSurvey || isMain) && (
            <ExportInstructionsSection
              isExportable={blockValues.isExportable}
              exportInstructions={blockValues.exportInstructions}
              handleFieldChange={handleFieldChange}
            />
          )}
        </>
      )}
    </div>
  );
};

interface BlockFormProps {
  mainSurveyStore: MainSurveyStore;
  isSubBlock?: boolean;
  handleAfterDeleteBlock?: (blockId: string) => void;
  setIsAwaitingBlockUpdate?: (isAwaiting: boolean) => void;
}
